import axios from 'axios';

export const MEDIA_CHUNK_BYTES = 8 * 1024 * 1024;
const MAX_CHUNK_ATTEMPTS = 3;

const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(resolve, milliseconds);
  signal?.addEventListener('abort', () => {
    clearTimeout(timer);
    reject(new DOMException('Upload cancelled.', 'AbortError'));
  }, { once: true });
});

const requestConfig = (workspaceId, signal, extra = {}) => {
  const { headers = {}, ...rest } = extra;
  return {
    ...rest,
    withCredentials: true,
    signal,
    headers: { 'x-workspace-id': workspaceId, ...headers }
  };
};

export async function uploadMediaResumable({ apiBase, file, workspaceId, onProgress, signal }) {
  if (!file || !workspaceId) throw new Error('Select a workspace and media file before uploading.');

  const { data: created } = await axios.post(`${apiBase}/api/media/uploads`, {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size
  }, requestConfig(workspaceId, signal));

  const uploadId = created.uploadId;
  let offset = Number(created.offset || 0);
  onProgress?.(Math.max(1, Math.floor((offset / Math.max(file.size, 1)) * 100)));

  while (offset < file.size) {
    const chunkOffset = offset;
    const chunk = file.slice(chunkOffset, Math.min(chunkOffset + MEDIA_CHUNK_BYTES, file.size));
    let completed = null;

    for (let attempt = 1; attempt <= MAX_CHUNK_ATTEMPTS; attempt += 1) {
      try {
        const response = await axios.patch(`${apiBase}/api/media/uploads/${encodeURIComponent(uploadId)}`, chunk, requestConfig(workspaceId, signal, {
          headers: {
            'Content-Type': 'application/octet-stream',
            'Upload-Offset': String(chunkOffset),
            'Upload-Length': String(chunk.size)
          },
          onUploadProgress(event) {
            const sent = Math.min(Number(event.loaded || 0), chunk.size);
            onProgress?.(Math.min(99, Math.floor(((chunkOffset + sent) / Math.max(file.size, 1)) * 100)));
          }
        }));
        completed = response.data;
        break;
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError' || signal?.aborted) throw error;
        try {
          const status = await axios.get(`${apiBase}/api/media/uploads/${encodeURIComponent(uploadId)}`, requestConfig(workspaceId, signal));
          if (status.data.completed) return status.data.upload;
          if (Number(status.data.offset) > chunkOffset) {
            offset = Number(status.data.offset);
            completed = status.data;
            break;
          }
        } catch (statusError) {
          if (statusError?.name === 'CanceledError' || statusError?.name === 'AbortError' || signal?.aborted) throw statusError;
        }
        if (attempt === MAX_CHUNK_ATTEMPTS) throw error;
        await wait(attempt * 700, signal);
      }
    }

    if (completed?.completed && completed.upload) {
      onProgress?.(100);
      return completed.upload;
    }
    offset = Number(completed?.offset ?? (offset + chunk.size));
    onProgress?.(Math.min(99, Math.floor((offset / Math.max(file.size, 1)) * 100)));
  }

  const status = await axios.get(`${apiBase}/api/media/uploads/${encodeURIComponent(uploadId)}`, requestConfig(workspaceId, signal));
  if (!status.data.completed || !status.data.upload) throw new Error('Upload verification did not finish. Retry the upload.');
  onProgress?.(100);
  return status.data.upload;
}
